import type { ReactNode } from "react";

/**
 * A titled group of label/value pairs for detail views (design.md §5.2
 * campaign detail header/sections). One shared shape instead of each detail
 * screen hand-rolling its own `<dl>` markup.
 */
export function DetailGroup({
  title,
  items,
  layout = "rows",
  footer,
}: {
  title: string;
  // `mono` is an opt-in per-item override (primitives.css's
  // `.v2-detail-group dd` resets to the normal UI font — see that rule's own
  // docstring) — the prototype marks only SOME values as monospace (e.g.
  // Contact detail's Email, Campaign detail's Plantilla (ID)/Segmento (ID)),
  // never a whole DetailGroup.
  // `span` is an opt-in per-item override, only meaningful under
  // `layout="grid"` — the reference's own "Datos de la plantilla" grid
  // (`Panel Precision App.dc.html` line 1175: `grid-column:span 2`) gives
  // its "Adjuntos" item 2 of the 3 columns, unlike every other item there.
  items: readonly { label: string; value: ReactNode; mono?: boolean; span?: 2 }[];
  // "rows" (default): label left / value right, one full-width row per item
  // — every existing caller (Contacts/Segments/Templates/Operational
  // Health). "grid": label above value, 3 equal columns per row — the
  // reference prototype's own "Datos de la campaña" layout
  // (`Panel Precision App.dc.html` line 394), opt-in per caller so it never
  // changes the other screens' already-correct 2-column layout.
  layout?: "rows" | "grid";
  // panel-redesign-v3 T5 (REDISENO-PANEL.md §5.3): optional trailing content
  // below the rows, inside the same region — the campaign detail's
  // "Contenido" card's "Ver plantilla →" link. Omitted by every other
  // existing caller, so it changes nothing for them.
  footer?: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className={layout === "grid" ? "v2-detail-group v2-detail-group--grid" : "v2-detail-group"}
    >
      <h2>{title}</h2>
      <dl>
        {items.map((item) => (
          <div
            key={item.label}
            className={
              item.span === 2 ? "v2-detail-group__row v2-detail-group__row--span-2" : "v2-detail-group__row"
            }
          >
            <dt>{item.label}</dt>
            <dd className={item.mono ? "v2-detail-group__value--mono" : undefined}>{item.value}</dd>
          </div>
        ))}
      </dl>
      {footer}
    </section>
  );
}
